import React from 'react';
import HRLogo from './HRLogo';

/**
 * Trust Anchor Footer for HumanReplica.ai
 * 
 * Purpose: This footer is not navigation and not marketing.
 * It is a final institutional reassurance layer that communicates:
 * - "This is a real company."
 * - "This system is governed."
 * - "Responsibility is explicit."
 */
const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="bg-hig-bg-secondary border-t border-hig-border relative z-10">
      {/* Main Footer Content */}
      <div className="container-hig py-12 md:py-16">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-10 md:gap-12">

          {/* SECTION A — Identity & Category (Left Column) */}
          <div className="space-y-4">
            <div className="flex items-center gap-2.5">
              <HRLogo state="sovereign" size={20} showGlow={false} />
              <h3 className="text-lg font-semibold text-hig-text-primary tracking-tight">
                HumanReplica.ai®
              </h3>
            </div>
            <p className="text-sm text-hig-text-secondary leading-relaxed max-w-xs">
              Governed human-trained AI Replicas with mandatory human oversight.
            </p>
            {/* Social Links */}
            <div className="flex items-center gap-4 pt-1">
              <a
                href="https://x.com/HumanReplica_ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Follow HumanReplica.ai on X"
                className="flex items-center gap-1.5 text-xs text-hig-text-tertiary hover:text-hig-text-primary transition-colors group"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  className="opacity-60 group-hover:opacity-100 transition-opacity"
                >
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-4.714-6.231-5.401 6.231H2.744l7.737-8.835L1.254 2.25H8.08l4.259 5.63L18.244 2.25zm-1.161 17.52h1.833L7.084 4.126H5.117L17.083 19.77z" />
                </svg>
                <span>@HumanReplica_ai</span>
              </a>
              <a
                href="https://www.linkedin.com/company/humanreplica-ai/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="HumanReplica.ai on LinkedIn"
                className="flex items-center gap-1.5 text-xs text-hig-text-tertiary hover:text-hig-text-primary transition-colors group"
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                  className="opacity-60 group-hover:opacity-100 transition-opacity"
                >
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 0 1-2.063-2.065 2.064 2.064 0 1 1 2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                </svg>
                <span>LinkedIn</span>
              </a>
            </div>
          </div>

          {/* SECTION B — Governance Guarantees (Center Column) */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-hig-text-primary uppercase tracking-wider">
              Governance by Design
            </h4>
            <ul className="space-y-2.5">
              <li className="flex items-start gap-2">
                <span className="text-hig-text-tertiary text-xs mt-0.5">•</span>
                <span className="text-sm text-hig-text-secondary">Human-in-Command by Design</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-hig-text-tertiary text-xs mt-0.5">•</span>
                <span className="text-sm text-hig-text-secondary">Decision Guardian™ Safeguard</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-hig-text-tertiary text-xs mt-0.5">•</span>
                <span className="text-sm text-hig-text-secondary">Full Auditability of Actions</span>
              </li>
              <li className="flex items-start gap-2">
                <span className="text-hig-text-tertiary text-xs mt-0.5">•</span>
                <span className="text-sm text-hig-text-secondary">Human Accountability Always Required</span>
              </li>
            </ul>
            {/* Behavioral Promise */}
            <p className="text-xs text-hig-text-tertiary italic pt-2 border-t border-hig-divider">
              Critical or ambiguous actions are paused and escalated to a human.
            </p>
          </div>

          {/* SECTION C — Trust & Responsibility Links (Right Column) */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium text-hig-text-primary uppercase tracking-wider">
              Trust & Responsibility
            </h4>
            <ul className="space-y-2.5">
              <li>
                <a
                  href="/privacy"
                  className="text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                >
                  Privacy Notice (Pre-Launch)
                </a>
              </li>
              <li>
                <a
                  href="/privacy"
                  className="text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                >
                  Responsible Use
                </a>
              </li>

              <li>
                <a
                  href="/privacy"
                  className="text-sm text-hig-text-secondary hover:text-hig-text-primary transition-colors"
                >
                  Privacy Policy
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* SECTION D — Compliance & Readiness (Subtle bottom strip) */}
        <div className="mt-10 pt-6 border-t border-hig-divider">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            {/* Compliance Indicators */}
            <div className="flex items-center gap-4 text-xs text-hig-text-tertiary">
              <span>GDPR Ready</span>
              <span className="text-hig-border">•</span>
              <span>SOC 2–Aligned</span>
              <span className="text-hig-border">•</span>
              <span>Data Protection by Design</span>
            </div>

            {/* Copyright */}
            <p className="text-xs text-hig-text-tertiary">
              © {currentYear} HumanReplica.ai®
            </p>
          </div>
        </div>

        {/* SECTION E — Ownership Closure Statement (Final Line) */}
        <div className="mt-10 pt-8 border-t border-hig-divider text-center">
          <p className="text-sm text-hig-text-primary/90 font-semibold tracking-wide">
            Every Human Replica is owned, trained, and governed by a real human.
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;